import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [wishlistItems, setWishlistItems] = useState([]);
  const isInitialized = useRef(false);
  const skipUrlUpdate = useRef(false);

  // Update URL with wishlist items (encode items with selections)
  const updateUrl = useCallback((items) => {
    if (skipUrlUpdate.current) return;
    
    const params = new URLSearchParams();
    if (items.length > 0) {
      // Encode items with selections for URL
      // Format: base64 encoded JSON array of {title, selections}
      try {
        const savedItems = JSON.parse(localStorage.getItem('wishlist') || '[]');
        const urlItems = items.map(itemId => {
          // Find full item data from localStorage
          const fullItem = savedItems.find(i => (i.wishlistId || i.title) === itemId);
          if (fullItem && fullItem.selections) {
            // Include selections in URL (base64 encode selections JSON)
            const selectionStr = btoa(JSON.stringify(fullItem.selections));
            return `${fullItem.title}::${selectionStr}`;
          }
          return itemId; // Fallback to just title if no selections
        });
        // Join items with comma - URLSearchParams will handle encoding
        params.set('items', urlItems.join(','));
      } catch (error) {
        console.error('Error encoding items for URL:', error);
        // Fallback to simple title list
        params.set('items', items.join(','));
      }
    }
    
    const newSearch = params.toString();
    const newPath = location.pathname + (newSearch ? `?${newSearch}` : '');
    const currentPath = location.pathname + location.search;
    
    // Only navigate if the URL actually changed
    if (newPath !== currentPath) {
      skipUrlUpdate.current = true;
      navigate(newPath, { replace: true });
      setTimeout(() => {
        skipUrlUpdate.current = false;
      }, 100);
    }
  }, [location.pathname, location.search, navigate]);

  // Initialize wishlist from URL or localStorage on mount
  useEffect(() => {
    if (isInitialized.current) return;
    
    const params = new URLSearchParams(location.search);
    const itemsParam = params.get('items') || params.get('w');
    
    if (itemsParam) {
      // Load from URL (shared wishlist or user's own wishlist with URL)
      try {
        // Decode URL-encoded items
        const decodedItems = decodeURIComponent(itemsParam);
        const itemStrings = decodedItems.split(',').filter(Boolean);
        const itemIds = [];
        const itemsWithSelections = [];
        
        // Parse items from URL (may include selections)
        itemStrings.forEach(itemStr => {
          if (itemStr.includes('::')) {
            // Item has selections encoded
            const [title, selectionBase64] = itemStr.split('::');
            try {
              const selections = JSON.parse(atob(selectionBase64));
              const itemId = `${title}::${selectionBase64}`;
              itemIds.push(itemId);
              itemsWithSelections.push({ title, selections, wishlistId: itemId });
            } catch (error) {
              console.error('Error decoding selections:', error);
              itemIds.push(title);
            }
          } else {
            // Simple title without selections
            itemIds.push(itemStr);
          }
        });
        
        console.log('Loading wishlist from URL:', itemIds);
        setWishlistItems(itemIds);
        
        // Save items with selections to localStorage
        if (itemsWithSelections.length > 0 || itemIds.length > 0) {
          try {
            const savedItems = JSON.parse(localStorage.getItem('wishlist') || '[]');
            const existingIds = savedItems.map(item => item.wishlistId || item.title);
            
            // Add items with selections
            itemsWithSelections.forEach(item => {
              if (!existingIds.includes(item.wishlistId)) {
                // Try to find base item data
                const baseItem = savedItems.find(i => i.title === item.title && !i.selections);
                if (baseItem) {
                  savedItems.push({ ...baseItem, ...item });
                } else {
                  savedItems.push({ ...item, desc: item.title, context: '' });
                }
              }
            });
            
            // Add simple items without selections
            itemIds.forEach(itemId => {
              if (!itemId.includes('::') && !existingIds.includes(itemId)) {
                const existingItem = savedItems.find(item => item.title === itemId);
                if (!existingItem) {
                  savedItems.push({ title: itemId, desc: itemId, context: '' });
                }
              }
            });
            
            localStorage.setItem('wishlist', JSON.stringify(savedItems));
          } catch (error) {
            console.error('Error updating localStorage from URL:', error);
          }
        }
        isInitialized.current = true;
      } catch (error) {
        console.error('Error parsing wishlist from URL:', error);
        setWishlistItems([]);
        isInitialized.current = true;
      }
    } else {
      // Load from localStorage (user's own wishlist)
      const savedWishlist = localStorage.getItem('wishlist');
      if (savedWishlist) {
        try {
          const items = JSON.parse(savedWishlist);
          const titles = items.map(item => item.title).filter(Boolean);
          console.log('Loading wishlist from localStorage:', titles);
          setWishlistItems(titles);
          // Update URL with current wishlist
          if (titles.length > 0) {
            skipUrlUpdate.current = true;
            updateUrl(titles);
            setTimeout(() => {
              skipUrlUpdate.current = false;
            }, 100);
          }
        } catch (error) {
          console.error('Error parsing saved wishlist:', error);
          setWishlistItems([]);
        }
      }
      isInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - intentionally exclude location.search and updateUrl

  // Also listen for URL changes (for shared wishlists)
  useEffect(() => {
    if (!isInitialized.current) return;
    
    const params = new URLSearchParams(location.search);
    const itemsParam = params.get('items') || params.get('w');
    
    if (itemsParam) {
      // Decode URL-encoded items
      const decodedItems = decodeURIComponent(itemsParam);
      const itemStrings = decodedItems.split(',').filter(Boolean);
      const itemIds = itemStrings.map(itemStr => {
        // Keep the full string (including selections if present)
        return itemStr;
      });
      
      // Update state if URL has different items
      setWishlistItems(prev => {
        const prevStr = prev.sort().join(',');
        const urlStr = itemIds.sort().join(',');
        if (prevStr !== urlStr) {
          console.log('URL changed, updating wishlist from URL:', itemIds);
          return itemIds;
        }
        return prev;
      });
    }
  }, [location.search]); // Update when URL search params change

  // Sync URL when wishlist items change (always update URL for sharing)
  useEffect(() => {
    if (!isInitialized.current || skipUrlUpdate.current) {
      return;
    }
    
    console.log('Wishlist items changed:', wishlistItems);
    console.log('Current pathname:', location.pathname);
    
    // Always update URL when on wishlist page, or update when items change
    // This ensures the URL always reflects the current wishlist state for sharing
    if (location.pathname === '/wishlist') {
      updateUrl(wishlistItems);
    } else if (wishlistItems.length > 0) {
      // If not on wishlist page but items exist, update URL in background
      // This ensures URL is ready when user navigates to wishlist page
      const currentParams = new URLSearchParams(location.search);
      const currentItems = (currentParams.get('items') || currentParams.get('w') || '').split(',').filter(Boolean);
      const currentStr = currentItems.sort().join(',');
      const newStr = wishlistItems.sort().join(',');
      
      if (currentStr !== newStr) {
        // URL needs updating, but we'll do it when user visits wishlist page
        console.log('Wishlist items changed, will update URL on wishlist page');
      }
    }
  }, [wishlistItems, location.pathname, location.search, updateUrl]); // Update when items change or path changes

  // Add item to wishlist
  const addToWishlist = (item) => {
    const { title, wishlistId } = item;
    const itemId = wishlistId || title; // Use wishlistId if provided (includes selections), otherwise use title
    
    if (!title) {
      console.warn('Cannot add item to wishlist: missing title', item);
      return;
    }

    console.log('Adding to wishlist:', itemId, item);

    setWishlistItems(prev => {
      if (prev.includes(itemId)) {
        console.log('Item already in wishlist:', itemId);
        return prev; // Already in wishlist
      }
      const updated = [...prev, itemId];
      console.log('Updated wishlist items:', updated);
      
      // Save to localStorage for user's own wishlist (store full item data with selections)
      try {
        const savedItems = JSON.parse(localStorage.getItem('wishlist') || '[]');
        // Remove old entry with same wishlistId (which includes selections) if exists
        // Only remove by wishlistId, not by title, to allow multiple items with same title but different selections
        const filtered = savedItems.filter(i => (i.wishlistId || i.title) !== itemId);
        
        // Ensure all image properties and sourceRoute are preserved when saving
        const itemToSave = {
          ...item,
          // Explicitly preserve all image properties
          Img: item.Img || item.ImgJpg || item.ImgJpeg || '',
          ImgJpg: item.ImgJpg || item.Img || item.ImgJpeg || '',
          ImgJpeg: item.ImgJpeg || item.Img || item.ImgJpg || '',
          // Preserve sourceRoute if it exists
          sourceRoute: item.sourceRoute || null,
        };
        
        filtered.push(itemToSave); // Add new item with selections and all image properties
        localStorage.setItem('wishlist', JSON.stringify(filtered));
        console.log('Saved to localStorage - item with images:', {
          title: itemToSave.title,
          Img: itemToSave.Img,
          ImgJpg: itemToSave.ImgJpg,
          ImgJpeg: itemToSave.ImgJpeg,
          wishlistId: itemToSave.wishlistId
        });
      } catch (error) {
        console.error('Error saving to localStorage:', error);
      }
      
      // If on wishlist page, update URL immediately for sharing
      if (location.pathname === '/wishlist' && isInitialized.current) {
        skipUrlUpdate.current = true;
        updateUrl(updated);
        setTimeout(() => {
          skipUrlUpdate.current = false;
        }, 50);
      }
      
      return updated;
    });
  };

  // Remove item from wishlist (by wishlistId or title)
  const removeFromWishlist = (itemId) => {
    setWishlistItems(prev => {
      const updated = prev.filter(item => item !== itemId);
      
      // Remove from localStorage
      const savedItems = JSON.parse(localStorage.getItem('wishlist') || '[]');
      const filtered = savedItems.filter(item => 
        (item.wishlistId || item.title) !== itemId
      );
      localStorage.setItem('wishlist', JSON.stringify(filtered));
      
      return updated;
    });
  };

  // Check if item is in wishlist (by wishlistId or title)
  const isInWishlist = (itemId) => {
    return wishlistItems.includes(itemId);
  };

  // Get wishlist count
  const getWishlistCount = () => {
    return wishlistItems.length;
  };

  // Clear wishlist
  const clearWishlist = () => {
    setWishlistItems([]);
    localStorage.removeItem('wishlist');
    updateUrl([]);
  };

  // Get shareable URL (with selections encoded)
  const getShareableUrl = () => {
    if (wishlistItems.length === 0) return window.location.origin + '/wishlist';
    
    try {
      const savedItems = JSON.parse(localStorage.getItem('wishlist') || '[]');
      const urlItems = wishlistItems.map(itemId => {
        // Find full item data from localStorage
        const fullItem = savedItems.find(i => (i.wishlistId || i.title) === itemId);
        if (fullItem && fullItem.selections) {
          // Include selections in URL (base64 encode selections JSON)
          const selectionStr = btoa(JSON.stringify(fullItem.selections));
          return `${fullItem.title}::${selectionStr}`;
        }
        return itemId; // Fallback to just title if no selections
      });
      const itemsParam = urlItems.join(',');
      // Use URLSearchParams to properly encode
      const params = new URLSearchParams();
      params.set('items', itemsParam);
      return `${window.location.origin}/wishlist?${params.toString()}`;
    } catch (error) {
      console.error('Error creating shareable URL:', error);
      // Fallback to simple title list
      const params = new URLSearchParams();
      params.set('items', wishlistItems.join(','));
      return `${window.location.origin}/wishlist?${params.toString()}`;
    }
  };

  const value = {
    wishlistItems,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    getWishlistCount,
    clearWishlist,
    getShareableUrl,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};
